import React from "react";
import Close from "../../../Icons/Close";
import Minimize from "../../../Icons/Minimize";
import { completeSurvey } from "../../AppContainer";
import "./ChatWindow.css";
import ChatMessages, {replaceOngoingConversationUrlsWithLinks, replaceResponseUrlsWithLinks} from "./ChatMessages";
import Ajax from "../../../Models/Ajax";
import Conversation from "../../../Models/Conversation";
import {setCompleted} from "../../../Helpers/Storage";
import ChatInput from "./ChatInput";
import LiveConversationForm from "./LiveConversationForm/LiveConversationForm";

let conversationInitiated = false;
let connection = null;

const loadAcceptedAnswers = (acceptedAnswers, setPossibleAnswers) => {
  if (!acceptedAnswers) {
    setPossibleAnswers([]);
    return;
  }
  let possibleAnswers = [];
  switch (acceptedAnswers.type) {
  case "scale":
    if (acceptedAnswers.max == undefined || acceptedAnswers.min == undefined || (acceptedAnswers.max - acceptedAnswers.min) > 30) {
      break;
    }

    let i = acceptedAnswers.min;
    while (i <= acceptedAnswers.max) {
      possibleAnswers.push(i);
      i++;
    }
    break;
  case "yes_no":
    if (!acceptedAnswers.answers) {
      break;
    }
    possibleAnswers = acceptedAnswers.answers.map(e => e.display_value);
    break;
  case "single_select":
    if (!acceptedAnswers.answers || acceptedAnswers.answers.length > 30) {
      break;
    }
    const imageAnswers = acceptedAnswers.answers.filter((answer) => answer.image && answer.image.image_url != null);
    const hasImages = (imageAnswers.length > 0);
    // For this first iteration, we will not be using images quick selects
    if (hasImages) {
      break;
    }
    possibleAnswers = acceptedAnswers.answers.map(e => e.display_value);
  }
  setPossibleAnswers(possibleAnswers);
};

const initiateConversation = async(config, setConversation, setPossibleAnswers) => {
  await connection.initiate(config);

  // messages is the entity that represents the history of on going conversations (responses + answers)
  // it has to be treated differently because in order to ensure the order of messages,
  // both responses and answers need to be in the same array
  if (connection.ongoingChat && connection.ongoingChat.messages) {
    replaceOngoingConversationUrlsWithLinks(connection.ongoingChat.messages);
    setConversation(Conversation.restoreConversation(connection.ongoingChat.messages));
    loadAcceptedAnswers(connection.ongoingChat.acceptedAnswers, setPossibleAnswers);
  } else {
    replaceResponseUrlsWithLinks(connection);
    setConversation(Conversation.addResponses(connection.ongoingChat.responses));
    loadAcceptedAnswers(connection.ongoingChat.accepted_answers, setPossibleAnswers);
  }
};

const initiateConnection = async(config, setIsLiveConversationSurvey, setLiveConversationConfiguration, setConversation, setPossibleAnswers) => {
  connection = new Ajax(config);

  const result = await connection.getSurveyConfiguration();

  const hasLiveConversationConfiguration = result.hasOwnProperty("live_conversation_configuration");

  setIsLiveConversationSurvey(hasLiveConversationConfiguration);
  setLiveConversationConfiguration(result.live_conversation_configuration);

  if (!conversationInitiated && !hasLiveConversationConfiguration) {
    await initiateConversation(config, setConversation, setPossibleAnswers);
    conversationInitiated = true;
  }
};

const ChatWindow = ({ config, inputPlaceholder, onClose, onMinimize }) => {
  const [conversation, setConversation] = React.useState(Conversation.getSortedConversation());
  const [possibleAnswers, setPossibleAnswers] = React.useState([]);
  const [replyValue, setReplyValue] = React.useState();
  const [chatInputDisabled, disableChatInput] = React.useState(false);
  const [isLiveConversationSurvey, setIsLiveConversationSurvey] = React.useState(false);
  const [liveConversationConfiguration, setLiveConversationConfiguration] = React.useState({});
  const [textareaHeight, setTextareaHeight] = React.useState(60);
  const chatInputRef = React.useRef();

  React.useEffect(() => {
    if(!conversationInitiated) {
      initiateConnection(config, setIsLiveConversationSurvey, setLiveConversationConfiguration, setConversation, setPossibleAnswers);
    }

    document.addEventListener("chatter-reset-chat", () => {
      Conversation.resetConversation();
      conversationInitiated = false;
    });
  }, []);

  //Input handles
  const sendReplyQuestion = async(replyValue) => {
    setConversation(Conversation.addAnswer(replyValue));
    setReplyValue("");
    setPossibleAnswers([]);
    chatInputRef.current.focus();
    setTextareaHeight(60);
    const resp = await connection.sendAnswer(replyValue);
    replaceResponseUrlsWithLinks(resp);
    setConversation(Conversation.addResponses(resp.responses));
    loadAcceptedAnswers(resp.accepted_answers, setPossibleAnswers);
    if (resp.complete && !config.keep_alive_after_completion) {
      setCompleted();
      disableChatInput(true);
      setTimeout(() => window.chatter.completeAndUnmount(), 10000);
    }
  };

  const onTextChange = (e, replyValue) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      sendReplyQuestion(replyValue);
      return;
    }

    const el = e.target;
    if (replyValue == "") {
      setTextareaHeight(60);
    } else {
      setTextareaHeight(el.scrollHeight < 170 ? el.scrollHeight : 170);
    }
  };

  return (
    <div className="chat-container">
      <div className="chat-top-bar">
        <div className="chat-logo" />
        <div className="chat-close-icon-button">
          <span onClick={onMinimize} ><Minimize /></span>
          <span onClick={onClose} ><Close className={"close-icon"} /></span>
        </div>
      </div>
      {isLiveConversationSurvey ?
        <LiveConversationForm
          config={config}
          live_conversation_config={liveConversationConfiguration}
        />
        :
        <>
          <ChatMessages
            conversation={conversation}
            possibleAnswers={possibleAnswers}
            sendReplyQuestion={sendReplyQuestion}
          />
          <ChatInput
            inputPlaceholder={inputPlaceholder}
            textareaHeight={textareaHeight}
            chatInputRef={chatInputRef}
            replyValue={replyValue}
            setReplyValue={setReplyValue}
            chatInputDisabled={chatInputDisabled}
            sendReplyQuestion={sendReplyQuestion}
            onTextChange={onTextChange}
          />
        </>
      }
    </div>
  );
};

export default ChatWindow;
